import { useCallback } from 'react'
import log from 'loglevel'
import {
  Int32Value,
  StringValue,
} from 'google-protobuf/google/protobuf/wrappers_pb'
import { VendorPromiseClient } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_grpc_web_pb'
import {
  CreateVendorEvaluationCriteriaRequest,
  DeleteVendorEvaluationCriteriaRequest,
  UpdateVendorEvaluationCriteriaRequest,
} from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_pb'
import { useAuthorizedGrpcClient } from '../../../../adapter'
import { useRefreshAfterEvaluationCriteriaSave } from './useVendorEvaluationCriteria'

export const useCreateVendorEvaluationCriteria = (): ((args: {
  vendorId: string
  title: string
  body: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(VendorPromiseClient)
  const refresh = useRefreshAfterEvaluationCriteriaSave()

  return useCallback(
    async ({ vendorId, title, body }) => {
      try {
        const request = new CreateVendorEvaluationCriteriaRequest()
          .setVendorId(vendorId)
          .setTitle(title)
          .setBody(body)
        await client.createVendorEvaluationCriteria(request)
      } catch (error) {
        log.error('Error creating vendor evaluation criteria', error)
        throw error
      } finally {
        await refresh()
      }
    },
    [client, refresh],
  )
}

export const useUpdateVendorEvaluationCriteria = (): ((args: {
  vendorId: string
  evaluationCriteriaId: string
  title?: string
  body?: string
  position?: number
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(VendorPromiseClient)
  const refresh = useRefreshAfterEvaluationCriteriaSave()

  return useCallback(
    async ({ vendorId, evaluationCriteriaId, title, body, position }) => {
      try {
        const request = new UpdateVendorEvaluationCriteriaRequest()
          .setVendorId(vendorId)
          .setEvaluationCriteriaId(evaluationCriteriaId)
        if (title !== undefined) {
          request.setTitle(new StringValue().setValue(title))
        }
        if (body !== undefined) {
          request.setBody(new StringValue().setValue(body))
        }
        if (position !== undefined) {
          request.setPosition(new Int32Value().setValue(position))
        }
        await client.updateVendorEvaluationCriteria(request)
      } catch (error) {
        log.error('Error updating vendor evaluation criteria', error)
        throw error
      } finally {
        await refresh()
      }
    },
    [client, refresh],
  )
}

export const useDeleteVendorEvaluationCriteria = (): ((args: {
  vendorId: string
  evaluationCriteriaId: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(VendorPromiseClient)
  const refresh = useRefreshAfterEvaluationCriteriaSave()

  return useCallback(
    async ({ vendorId, evaluationCriteriaId }) => {
      try {
        const request = new DeleteVendorEvaluationCriteriaRequest()
          .setVendorId(vendorId)
          .setEvaluationCriteriaId(evaluationCriteriaId)
        await client.deleteVendorEvaluationCriteria(request)
      } catch (error) {
        log.error('Error deleting vendor evaluation criteria', error)
        throw error
      } finally {
        await refresh()
      }
    },
    [client, refresh],
  )
}
